// THE WALL PANEL. What "Wall Panel" in the menu opens, on both shells.
//
// A sheet of shortcut buttons for the current location — the same idea as
// Favourites, except the buttons are ones the owner placed rather than devices
// they starred, and a button can be a scene, a fan ladder or a curtain cluster
// rather than only a device.
//
// ── The three things on it ─────────────────────────────────────────────────
//   ⚙  settings   → /wall/setup, the ONE editor: how many buttons, and what
//                   each one does.
//   ◉  wall mode  → this tablet opens on this panel from now on.
//   ✕  exit       → asks for the account password, then returns to the app.
//
// ⚠️ EXIT DOES NOT DISARM WALL MODE. You give the password, you get the app,
// and the next launch comes back here — that is the whole point of a screen
// screwed to a wall. Turning it off is a deliberate act on the switch above,
// not a side effect of walking past it once.
//
// ⚠️ AN EMPTY PANEL SENDS YOU TO SETTINGS instead of showing an empty grid: on
// a first run there is nothing to press and nothing to explain, and the only
// useful thing to do is place some buttons.
//
// Also: keeps the screen awake (Screen Wake Lock API), a tap-to-wake black
// overlay, and requests landscape where the browser allows it.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, IconButton, Button, CircularProgress, Switch, FormControlLabel,
  Tooltip, Slider,
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useApp } from '../../../../core/state/AppContext';
import { fetchPanels, runSequence } from './wallApi';
import {
  panelsForLocation, parseLocationPanel, isLocationPanel, normaliseSize,
  columnsFor, rowsFor, layoutButtons,
} from '../../../../core/wall/wallLocation';
import { loadKiosk, setKiosk } from '../../../../core/wall/kioskMode';
import {
  toggleTarget, isToggleOn, pressButton, fanTarget, fanSpeedOf, fanStepsFor,
  curtainTarget, lockTarget, lightTarget, lightPresetsFor, CURTAIN_ACTIONS,
  CURTAIN_OPEN, CURTAIN_STOP, CURTAIN_CLOSE, meterTarget,
} from '../../../../core/wall/wallButtons';
// ⚠️ The panel drives a curtain through the SAME timed runner as the phone —
// see CurtainCluster. A raw publish here would give one motor two behaviours.
import useCurtain from '../../../../core/hooks/useCurtain';
import { publishCommand } from '../../../../core/utils/deviceController';
// The current state, once, at open. MQTT alone only reports CHANGES — see the
// note on the subscription effect.
import { getDeviceStatus } from '../../../../core/api/api';
import { getStatusCached, ONLINE } from '../../../../core/devices/deviceStatus';
// A colour light's five channels — seed, live follow and debounced publish, the
// same hook the light screens use. See LightMixer.
import { useLightChannels } from '../../../../core/hooks/useLightChannels';
// The meter's readings — CurrentSense rows, not MQTT. See MeterTile.
import { fetchDay, parseLocal, downsample } from '../../../../core/devices/phase';
import { ResponsiveContainer, LineChart, Line, YAxis } from 'recharts';
import LockIcon from '@mui/icons-material/Lock';
import { connectMqtt, subscribeTopic, unsubscribeTopic, addMessageListener } from '../../../../core/mqtt/mqttClient';
import { showToast } from '../../../shared/toast';

// ── The two colours a toggle can be ────────────────────────────────────────
// Chosen to sit on the panel's near-black background: bright enough to read
// across a room, muted enough that a wall of sixteen "off" tiles is not a wall
// of alarms.
const TOGGLE_ON = '#2E7D32';    // green — the load is drawing
const TOGGLE_OFF = '#B4564F';   // soft red — the load is off
const NEUTRAL = '#37474F';      // slate — no state to show, or not a toggle
const EMPTY_SLOT = 'rgba(255,255,255,0.04)';

/**
 * A tile's background.
 *
 * Only a button with a TOGGLE TARGET and a KNOWN state gets a colour; anything
 * else keeps its configured `ButtonColor`. A momentary button, a scene or a
 * navigate tile has nothing to be on or off about, and painting one green would
 * be inventing a state it does not have.
 */
/**
 * The device a slot is about, whatever kind it is.
 *
 * Every kind stores its device the same way — the first action's UniqueNumber —
 * so the liveness dot does not need to know a fan from a lock from a scene. A
 * multi-step scene has several; the first is the one the tile is named for.
 */
const deviceOf = (button) => (button?.Actions || [])[0]?.UniqueNumber || null;

function toggleColor(button, target, on) {
  if (!button) return EMPTY_SLOT;
  if (!target || on === null) return button.ButtonColor || NEUTRAL;
  return on ? TOGGLE_ON : TOGGLE_OFF;
}

/*
 * ── Is the device there? ───────────────────────────────────────────────────
 *
 * A dot in the corner of every tile that has a device behind it: green when the
 * device has reported inside the liveness window, red when it has not, nothing
 * while the answer is still unknown.
 *
 * ⚠️ NOT DRAWN AS RED UNTIL IT IS KNOWN. The lookup is one request per device
 * and resolves one at a time, so painting "unknown" red would turn the whole
 * sheet red for a second on every open and then heal — which reads as an outage
 * that just fixed itself.
 *
 * `getStatusCached` is the SAME 45-second-cached lookup the device cards use, so
 * a panel of sixteen tiles driving four devices costs four requests, not
 * sixteen, and shares them with anything else on screen.
 */
function LiveDot({ uniqueNumber }) {
  const [state, setState] = useState(null);   // null | 'online' | 'offline'

  useEffect(() => {
    if (!uniqueNumber) return undefined;
    let alive = true;
    const read = () => {
      getStatusCached(uniqueNumber)
        .then((v) => {
          if (!alive) return;
          // '🟢' online · '🔴 <mins>' offline · '🔵' unknown
          if (v === ONLINE) setState('online');
          else if (typeof v === 'string' && v.startsWith('🔴')) setState('offline');
          else setState(null);
        })
        .catch(() => { if (alive) setState(null); });
    };
    read();
    // Longer than the 45 s cache TTL, so a re-check is a real read rather than
    // an echo of the one before it.
    const id = setInterval(read, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [uniqueNumber]);

  if (!state) return null;
  return (
    <Box sx={{
      position: 'absolute',
      top: 6,
      right: 6,
      width: 9,
      height: 9,
      borderRadius: '50%',
      bgcolor: state === 'online' ? '#4CAF50' : '#EF5350',
      // A ring, so the dot reads on a tile of any colour — including the green
      // one it would otherwise disappear into.
      boxShadow: '0 0 0 2px rgba(0,0,0,0.45)',
    }}
    />
  );
}

/*
 * ── A colour light is a MIXER, not six buttons ─────────────────────────────
 *
 * The presets alone can only ever reach six of the fixture's states. A WWRGB
 * lamp has FIVE independent channels at 0-255 each, so "Red" is one point in a
 * space the owner paid for and could not otherwise reach — no dimming, no warm
 * white at half, no red-plus-a-little-white.
 *
 * So the slot is a fader per channel, laid out like a mixing desk: vertical,
 * side by side, in the order the firmware expects them. Dragging one changes
 * that channel's intensity and leaves the others alone, which is exactly what
 * the hardware does.
 *
 * The presets stay underneath, smaller — they are the shortcuts, not the
 * instrument.
 *
 * ⚠️ CHANNEL ORDER IS THE FIRMWARE'S, and it is not alphabetical or spectral:
 * W · WW · R · G · B, confirmed against WWRGB_PRESETS where "White" is
 * `255,0,0,0,0` and "Blue" is `0,0,0,0,255`. Reordering the faders to look
 * nicer would send green when the user asked for red, and the firmware would
 * accept it.
 *
 * ⚠️ PUBLISHES ON RELEASE, NOT PER PIXEL. `setAndDebounce` moves the fader now
 * and sends once the hand stops — a frame per pointer move would flood the
 * broker and the fixture would lag behind the finger.
 */
const CHANNEL_LABELS = {
  wwrgb: ['W', 'WW', 'R', 'G', 'B'],
  rgb: ['R', 'G', 'B'],
  wwh: ['W', 'WW'],
};
// The colour each fader paints itself, so the desk is readable at a glance.
const CHANNEL_COLORS = {
  wwrgb: ['#FFFFFF', '#FFD08A', '#E53935', '#43A047', '#1E88E5'],
  rgb: ['#E53935', '#43A047', '#1E88E5'],
  wwh: ['#FFFFFF', '#FFD08A'],
};

function LightMixer({ button, target, userId }) {
  const labels = CHANNEL_LABELS[target.typeKey] || CHANNEL_LABELS.rgb;
  const colors = CHANNEL_COLORS[target.typeKey] || CHANNEL_COLORS.rgb;
  const count = labels.length;
  const {
    values, sendCommand, setAndDebounce,
  } = useLightChannels(target.uniqueNumber, count, userId);
  const presets = lightPresetsFor(target.typeKey);

  return (
    <Box sx={{
      position: 'relative',
      bgcolor: button.ButtonColor || '#37474F', color: button.TextColor || '#fff',
      borderRadius: 2, p: 0.75, minHeight: 0, minWidth: 0,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}
    >
      <LiveDot uniqueNumber={target.uniqueNumber} />
      <Box sx={{
        fontWeight: 700, fontSize: 'clamp(10px, 1.4vw, 15px)', textAlign: 'center',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pr: 1.5,
      }}
      >
        {button.ButtonText}
      </Box>

      {/* The faders. Vertical and side by side — a mixing desk, so five of them
          fit a square tile and each is grabbable without a label to read. */}
      <Box sx={{
        flex: 1, minHeight: 0, display: 'flex', justifyContent: 'space-evenly',
        alignItems: 'stretch', gap: 0.25, py: 0.5,
      }}
      >
        {labels.map((label, i) => (
          <Box key={label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
            <Slider
              orientation="vertical"
              size="small"
              min={0}
              max={255}
              value={Number(values[i]) || 0}
              onChange={(_, v) => {
                const next = [...values];
                next[i] = Number(v);
                setAndDebounce(next);
              }}
              sx={{
                flex: 1,
                color: colors[i],
                '& .MuiSlider-thumb': { width: 12, height: 12 },
                '& .MuiSlider-rail': { opacity: 0.28 },
              }}
            />
            <Box component="span" sx={{ fontSize: 9, opacity: 0.75, mt: 0.25, fontWeight: 700 }}>
              {label}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Shortcuts, deliberately small: the faders are the control now. */}
      <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap', justifyContent: 'center' }}>
        {presets.map((p) => (
          <Box
            key={p.label}
            onClick={() => sendCommand(p.command)}
            title={p.label.replace('\n', ' ')}
            sx={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              bgcolor: p.swatch,
              border: '1px solid rgba(255,255,255,0.45)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

/*
 * ── A current sensor is a READOUT, not a button ────────────────────────────
 *
 * The tile shows the last hours of load as a sparkline plus the latest reading.
 * No axes, no legend, no tooltip: it is read from across a room at the size of a
 * playing card, and everything else is furniture.
 *
 * ⚠️ THE READINGS ARE NOT MQTT. A switch reports its state on `<uno>/client`; a
 * meter writes rows into CurrentSense every ~30 s, which is what
 * core/devices/phase reads. So this polls rather than subscribing — on the same
 * 30 s cadence `fetchDay` already caches today's readings at, so a panel that
 * re-renders does not re-fetch.
 */
function MeterTile({ button, uniqueNumber }) {
  const [rows, setRows] = useState([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!uniqueNumber) return undefined;
    let alive = true;
    const read = () => {
      const now = new Date();
      const ds = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
      fetchDay(uniqueNumber, ds, true)
        .then((list) => {
          if (!alive) return;
          setFailed(false);
          // One line, even on a three-phase meter: the tile is too small for
          // three, and the question it answers is "is the load high right now".
          // The per-phase breakdown is a tap away on the device screen.
          const byTime = new Map();
          list.forEach((r) => {
            const t = parseLocal(r.DateTime).getTime();
            if (!Number.isFinite(t)) return;
            byTime.set(t, (byTime.get(t) || 0) + (Number(r.current) || 0));
          });
          const series = [...byTime.entries()]
            .sort((a, b) => a[0] - b[0])
            .map(([t, a]) => ({ t, a }));
          setRows(downsample(series, 60));
        })
        .catch(() => { if (alive) setFailed(true); });
    };
    read();
    const id = setInterval(read, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [uniqueNumber]);

  const latest = rows.length ? rows[rows.length - 1].a : null;
  const peak = rows.reduce((m, r) => Math.max(m, r.a), 0);

  return (
    <Box sx={{
      position: 'relative',
      bgcolor: button.ButtonColor || '#263238',
      color: button.TextColor || '#fff',
      borderRadius: 2, p: 0.75, display: 'flex', flexDirection: 'column',
      minHeight: 0, minWidth: 0, overflow: 'hidden',
    }}
    >
      <LiveDot uniqueNumber={uniqueNumber} />
      <Box sx={{
        fontWeight: 700, fontSize: 'clamp(10px, 1.4vw, 15px)', textAlign: 'center',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pr: 1.5,
      }}
      >
        {button.ButtonText}
      </Box>

      {/* The number, because a sparkline says "changing" and not "how much". */}
      <Box sx={{ textAlign: 'center', lineHeight: 1 }}>
        <Box component="span" sx={{ fontSize: 'clamp(16px, 3vw, 30px)', fontWeight: 300 }}>
          {latest == null ? '—' : latest.toFixed(2)}
        </Box>
        <Box component="span" sx={{ fontSize: '0.55em', opacity: 0.7, ml: 0.5 }}>A</Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, mt: 0.25 }}>
        {rows.length > 1 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 2, right: 2, bottom: 0, left: 2 }}>
              {/* Domain from 0, so a flat line near zero reads as "nothing is
                  drawing" rather than filling the tile with noise. */}
              <YAxis hide domain={[0, Math.max(1, peak * 1.15)]} />
              <Line
                type="monotone" dataKey="a" stroke="#4FC3F7" strokeWidth={2}
                dot={false} isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{
            height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, opacity: 0.55,
          }}
          >
            {failed ? 'no reading' : 'waiting for readings'}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// One slot holding several actions — a fan ladder, or Open/Stop/Close. The
// alternative is giving each action its own slot, which on a 4-button sheet
// spends the whole panel on one curtain.
function Cluster({
  button, steps, columns, rows, activeIndex = -1, onStep,
}) {
  return (
    <Box sx={{
      position: 'relative',
      bgcolor: button.ButtonColor || '#37474F', color: button.TextColor || '#fff',
      borderRadius: 2, p: 0.75, minHeight: 0, minWidth: 0,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}
    >
      <LiveDot uniqueNumber={deviceOf(button)} />
      <Box sx={{
        fontWeight: 700, fontSize: 'clamp(10px, 1.4vw, 15px)', textAlign: 'center',
        mb: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        pr: 1.5,
      }}
      >
        {button.ButtonText}
      </Box>
      <Box sx={{
        flex: 1, minHeight: 0, display: 'grid', gap: '3px',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
      }}
      >
        {steps.map((st, i) => (
          <Box
            key={st.message}
            onClick={() => onStep(st.message)}
            sx={{
              borderRadius: 1, display: 'flex', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer', userSelect: 'none',
              fontSize: 'clamp(9px, 1.2vw, 14px)', fontWeight: 700,
              minHeight: 0, minWidth: 0,
              // The running step is filled; the rest outlined. Unknown lights
              // nothing, the same rule a toggle follows.
              bgcolor: i === activeIndex ? 'rgba(255,255,255,0.92)' : 'transparent',
              color: i === activeIndex ? '#111' : 'inherit',
              border: i === activeIndex ? 'none' : '1px solid rgba(255,255,255,0.35)',
            }}
          >
            {st.label}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/*
 * A curtain or sliding gate slot.
 *
 * ⚠️ IT GOES THROUGH THE SAME RUNNER AS EVERY OTHER SURFACE, not a raw publish.
 * The panel is a wall control for the same motor the phone drives, and a press
 * here has to mean what a press there means: a timed run, a reversal that stops
 * first, and a repeat that is refused because at the device a repeat is a STOP
 * (see core/devices/curtainRunner). Publishing raw from here would give the
 * panel its own, different behaviour for the same curtain — and because the
 * runner is module-level, the two would also fight over one motor's timer.
 *
 * The step labels stay short: a wall tile is a third of a slot wide, so "Open"
 * rather than "Open Curtain".
 */
function CurtainCluster({ button, uniqueNumber }) {
  const curtain = useCurtain({ UniqueNumber: uniqueNumber });
  const act = {
    [CURTAIN_OPEN]: curtain.open,
    [CURTAIN_STOP]: curtain.stop,
    [CURTAIN_CLOSE]: curtain.close,
  };
  // The running direction is filled, the same way a fan ladder shows its speed.
  const activeIndex = CURTAIN_ACTIONS.findIndex(
    (a) => (a.message === CURTAIN_OPEN && curtain.opening)
      || (a.message === CURTAIN_CLOSE && curtain.closing),
  );
  return (
    <Cluster
      button={button}
      steps={CURTAIN_ACTIONS.map((a) => ({ label: a.label, message: a.message }))}
      columns={3}
      rows={1}
      activeIndex={activeIndex}
      onStep={(m) => act[m] && act[m]()}
    />
  );
}

export default function WallModeScreen() {
  const { userId, user, colors, locationId } = useApp();
  const navigate = useNavigate();
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [screenOff, setScreenOff] = useState(false);
  const [kioskOn, setKioskOn] = useState(() => loadKiosk().enabled);
  const wakeRef = useRef(null);

  /*
   * ⚠️ THE PANEL BELONGS TO A LOCATION, so which location is the only question
   * that matters here — and the answer differs by who is asking:
   *
   *   a wall tablet   the location it was PINNED to when wall mode was switched
   *                   on. It must not follow the header's picker: someone
   *                   browsing another site from their phone would otherwise
   *                   change what the tablet on the wall is showing.
   *   anyone else     the location currently selected in the header, so the menu
   *                   entry shows the sheet for the site you are looking at.
   */
  const kiosk = loadKiosk();
  const panelLocationId = (kiosk.enabled && kiosk.locationId != null)
    ? kiosk.locationId
    : locationId;

  const load = useCallback(async () => {
    setLoading(true);
    const all = await fetchPanels(userId);
    setPanels(panelLocationId != null
      ? panelsForLocation(all, panelLocationId)
      // No location to scope by — fall back to any non-location panel rather
      // than showing nothing at all.
      : all.filter((p) => !isLocationPanel(p)));
    setLoading(false);
  }, [userId, panelLocationId]);

  useEffect(() => { load(); }, [load]);

  /*
   * Nothing placed yet -> go and place something. `replace` so the back button
   * does not bounce between an empty panel and its settings.
   *
   * ⚠️ Guarded by a ref, and only AFTER loading. Without the guard, settings'
   * own "Open panel" button walks straight back into this redirect and the two
   * screens ping-pong; without the loading check it fires on the empty first
   * render, before the panels have arrived.
   */
  const sentToSetup = useRef(false);
  useEffect(() => {
    if (loading || sentToSetup.current) return;
    const empty = panels.length === 0
      || panels.every((p) => !(p.Buttons || []).length);
    if (!empty) return;
    sentToSetup.current = true;
    navigate('/wall/setup', { replace: true });
  }, [loading, panels, navigate]);

  // Switching wall mode on pins this tablet to the location it is showing, so
  // the two can never disagree.
  const toggleKiosk = (on) => {
    setKiosk(on, on ? panelLocationId : null, null);
    setKioskOn(on);
  };

  // Live state for the toggle buttons on show.
  //
  // Subscribes ONLY to the devices this sheet actually toggles — a wall panel
  // that watched everything would carry the whole broker for the sake of a few
  // lit buttons. Momentary buttons need nothing: a curtain does not report
  // "open" and a scene has no state to show.
  const [statuses, setStatuses] = useState({});   // uno -> latest status text
  const [lockAsk, setLockAsk] = useState(null);  // { button, target } awaiting a password

  // ⚠️ IN AN EFFECT, NOT IN RENDER. window.prompt blocks and has a side effect;
  // running it while rendering fires it during reconciliation — twice under
  // StrictMode — and a door that asks for its password twice, or opens on a
  // render, is not a door lock.
  //
  // window.prompt for the same reason exit uses it: a wall tablet has no way
  // past a modal browser prompt, which is exactly what is wanted here.
  useEffect(() => {
    if (!lockAsk) return;
    const entered = window.prompt(`Password to open ${lockAsk.button.ButtonText || 'the lock'}`);
    setLockAsk(null);
    if (entered == null) return;
    if (entered === String(user?.Password)) {
      publishCommand(lockAsk.target.uniqueNumber, lockAsk.target.message, userId);
    } else {
      showToast('Wrong password', { severity: 'error' });  // no OK to press
    }
  }, [lockAsk, user, userId]);
  useEffect(() => {
    const p = panels[page];
    if (!p) return undefined;
    const unos = [...new Set((p.Buttons || [])
      .map((b) => toggleTarget(b)?.uniqueNumber || fanTarget(b)?.uniqueNumber)
      .filter(Boolean))];
    if (!unos.length) return undefined;

    let alive = true;
    connectMqtt();
    const detach = addMessageListener((topic, payload) => {
      const t = String(topic);
      if (!t.endsWith('/client')) return;
      const uno = t.slice(0, t.indexOf('/'));
      if (unos.includes(uno)) setStatuses((cur) => ({ ...cur, [uno]: String(payload) }));
    });
    unos.forEach((u) => subscribeTopic(`${u}/client`));

    /*
     * ⚠️ SUBSCRIBING IS NOT KNOWING. MQTT only delivers on CHANGE, so a switch
     * that nobody has touched since the panel opened never sends anything —
     * and the tile sat on "—" (unknown) indefinitely. On a wall panel that is
     * the normal case: it is switched on in the morning and the lights have
     * been in whatever state they are in since last night.
     *
     * It also broke the PRESS, not just the picture. `pressButton` sends the
     * opposite of what it is showing and treats unknown as off, so the first
     * tap on an already-on light published "on" — the light did not move, and
     * the panel looked dead.
     *
     * So the current state is READ once, per device, and MQTT keeps it live
     * from there. Same two sources every other screen in the app uses.
     *
     * ⚠️ The seed must NEVER overwrite a live frame. A device that reports
     * while these requests are in flight is more current than the row they
     * return, so this only fills a slot that is still empty — the same
     * `cur || db` rule the touch cards use.
     */
    Promise.all(unos.map((u) => getDeviceStatus(u)
      .then((res) => {
        const row = Array.isArray(res?.data) ? res.data[0] : null;
        return [u, row && row.Status ? String(row.Status) : ''];
      })
      .catch(() => [u, ''])))
      .then((pairs) => {
        if (!alive) return;
        setStatuses((cur) => {
          const next = { ...cur };
          pairs.forEach(([u, s]) => {
            if (s && next[u] === undefined) next[u] = s;
          });
          return next;
        });
      });

    return () => {
      alive = false;
      detach();
      unos.forEach((u) => unsubscribeTopic(`${u}/client`));
    };
  }, [panels, page]);

  // Keep screen awake.
  useEffect(() => {
    let released = false;
    const acquire = async () => {
      try {
        if ('wakeLock' in navigator) wakeRef.current = await navigator.wakeLock.request('screen');
      } catch (e) { /* not supported / denied */ }
    };
    acquire();
    const onVis = () => { if (document.visibilityState === 'visible') acquire(); };
    document.addEventListener('visibilitychange', onVis);
    // Best-effort landscape lock (supported on some Android browsers in fullscreen).
    try { window.screen?.orientation?.lock?.('landscape').catch(() => {}); } catch (e) { /* ignore */ }
    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVis);
      try { wakeRef.current?.release?.(); } catch (e) { /* ignore */ }
      try { window.screen?.orientation?.unlock?.(); } catch (e) { /* ignore */ }
      if (released) wakeRef.current = null;
    };
  }, []);

  const goToPanelId = (panelId) => {
    const idx = panels.findIndex((p) => p.WallPanelId === panelId);
    if (idx >= 0) setPage(idx);
  };

  const onButton = (b, isOn) => {
    if (b.ButtonType === 'navigate') { goToPanelId(b.TargetWallPanelId); return; }
    // A toggle sends the opposite of what it is showing. Everything else runs
    // its stored sequence unchanged — curtains and scenes are fire-and-forget.
    if (toggleTarget(b)) { pressButton(b, { userId, isOn }); return; }
    runSequence(userId, b);
  };

  /*
   * ⚠️ THIS RETURNS TO THE APP; IT DOES NOT DISARM WALL MODE. `setKiosk` is
   * deliberately not called. A tablet on a wall that stopped being a wall panel
   * the first time somebody stepped out of it would have to be re-armed by hand
   * every day. Use the switch in the top bar to actually turn it off.
   */
  const exitWithPin = () => {
    const entered = window.prompt('Enter your password to leave the wall panel');
    if (entered == null) return;
    if (entered === String(user?.Password)) navigate('/home');
    else showToast('Wrong password', { severity: 'error' });
  };

  if (loading) {
    return <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#000' }}><CircularProgress /></Box>;
  }

  // Normally unreachable — the effect above has already redirected to settings.
  // Kept for the moment between the two, and for a redirect that cannot run.
  if (panels.length === 0) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, bgcolor: colors.back }}>
        <Typography sx={{ color: colors.text }} variant="h5">No buttons on this panel yet</Typography>
        <Button variant="contained" onClick={() => navigate('/wall/setup')}>Set up buttons</Button>
        <Button onClick={exitWithPin} sx={{ color: colors.text }}>Exit</Button>
      </Box>
    );
  }

  const panel = panels[page];


  // A LOCATION panel is a fixed sheet: ColumnsCount holds the BUTTON COUNT and
  // the grid is derived from it, with every slot rendered whether or not it has
  // a button — the layout must not reflow as buttons are added, because muscle
  // memory on a wall panel is the entire point.
  //
  // A personal panel keeps the original free-flow behaviour, where ColumnsCount
  // really is a column count.
  const asGrid = isLocationPanel(panel);
  const size = asGrid ? normaliseSize(panel.ColumnsCount) : 0;
  const cols = asGrid ? columnsFor(size) : Math.max(1, panel.ColumnsCount);
  const gridSlots = asGrid ? layoutButtons(panel.Buttons, size) : null;
  const title = asGrid
    ? (parseLocationPanel(panel.PanelName)?.name || panel.PanelName)
    : panel.PanelName;

  return (
    // Fixed column: title row, grid taking the rest, pager pinned. Nothing
    // scrolls — a wall-mounted screen has no scrollbar anyone can reach.
    <Box sx={{
      height: '100vh', bgcolor: panel.BackColor || colors.back,
      position: 'relative', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}
    >
      {/* Top bar: what it is, then screen-off · wall-mode switch · settings · exit. */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5 }}>
        <Typography sx={{ flex: 1, color: colors.text, fontWeight: 600, pl: 1 }}>{title}</Typography>

        <Tooltip title="Blank the screen">
          <IconButton onClick={() => setScreenOff(true)} sx={{ color: colors.text }}>
            <PowerSettingsNewIcon />
          </IconButton>
        </Tooltip>

        {/* The switch lives HERE, on the panel, rather than buried in settings:
            it is a property of THIS SCREEN — "this tablet is the wall panel" —
            and it is the thing you reach for while looking at the panel. */}
        <Tooltip title={kioskOn
          ? 'This screen opens on the panel — tap to stop'
          : 'Make this screen open on the panel every time'}
        >
          <FormControlLabel
            sx={{ mr: 0, ml: 0.5, color: colors.text }}
            control={(
              <Switch
                size="small"
                checked={kioskOn}
                onChange={(e) => toggleKiosk(e.target.checked)}
                sx={{ '& .MuiSwitch-track': { bgcolor: colors.text } }}
              />
            )}
            label={<Typography sx={{ fontSize: 12, color: colors.text }}>Wall mode</Typography>}
          />
        </Tooltip>

        <Tooltip title="Wall panel settings — buttons and what they do">
          <IconButton onClick={() => navigate('/wall/setup')} sx={{ color: colors.text }}>
            <SettingsIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Leave the panel (asks for your password)">
          <IconButton onClick={exitWithPin} sx={{ color: colors.text }}><CloseIcon /></IconButton>
        </Tooltip>
      </Box>

      {/* Button grid */}
      <Box sx={{
        display: 'grid', gap: 1.5, p: 1.5,
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        // A fixed sheet fills the screen exactly — equal rows, no scrolling.
        // The tablet is on a wall; anything below the fold is unreachable.
        // The sheet takes whatever height is left and divides it evenly. Tiles
        // shrink to the screen; the screen never grows to the tiles.
        ...(asGrid
          ? { gridTemplateRows: `repeat(${rowsFor(size)}, 1fr)`, flex: 1, minHeight: 0, overflow: 'hidden' }
          : { gridAutoRows: '90px', flex: 1, minHeight: 0, overflowY: 'auto' }),
        pb: 5,
      }}>
        {asGrid ? gridSlots.map((b, i) => {
          const target = b ? toggleTarget(b) : null;
          const on = target ? isToggleOn(b, statuses[target.uniqueNumber]) : null;
          const fan = b ? fanTarget(b) : null;

          const light = b ? lightTarget(b) : null;
          // A colour light is a fader per channel plus its presets. Presets
          // alone reach six states of a five-channel fixture — see LightMixer.
          if (light) {
            return (
              <LightMixer
                key={b.WallButtonId}
                button={b}
                target={light}
                userId={userId}
              />
            );
          }

          const curtain = b ? curtainTarget(b) : null;
          const lock = b ? lockTarget(b) : null;
          const meter = b ? meterTarget(b) : null;

          // A current sensor: the slot is a live graph, and there is nothing to
          // press. See MeterTile.
          if (meter) {
            return (
              <MeterTile key={b.WallButtonId} button={b} uniqueNumber={meter.uniqueNumber} />
            );
          }

          // Open / Stop / Close belong together: Open without Stop is a gate you
          // cannot halt half-way. One slot, three actions.
          if (curtain) {
            return (
              <CurtainCluster
                key={b.WallButtonId}
                button={b}
                uniqueNumber={curtain.uniqueNumber}
              />
            );
          }

          // A lock is the one button that must not be pressable in passing —
          // the panel is by the door, and anyone standing at it can reach it.
          if (lock) {
            return (
              <Box
                key={b.WallButtonId}
                onClick={() => setLockAsk({ button: b, target: lock })}
                sx={{
                  position: 'relative',
                  bgcolor: b.ButtonColor || '#5D4037', color: b.TextColor || '#fff',
                  borderRadius: 2, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', flexDirection: 'column', gap: 0.25,
                  p: 1, cursor: 'pointer', userSelect: 'none', fontWeight: 600,
                  minHeight: 0, minWidth: 0, overflow: 'hidden',
                  fontSize: 'clamp(12px, 2.2vw, 22px)',
                }}
              >
                <LiveDot uniqueNumber={deviceOf(b)} />
                <LockIcon sx={{ fontSize: '1.4em' }} />
                {b.ButtonText}
              </Box>
            );
          }

          // A fan takes the whole cell: six steps in one tile, so the ladder is
          // reachable without giving up five more slots to it.
          if (fan) {
            const speed = fanSpeedOf(b, statuses[fan.uniqueNumber]);
            // The ladder is built for THIS fan's channel — see fanStepsFor.
            const steps = fanStepsFor(fan.channel);
            return (
              <Cluster
                key={b.WallButtonId}
                button={b}
                steps={steps}
                columns={3}
                // Derived, not fixed at 2: a panel fan has nine buttons (off
                // plus eight steps) where a standalone fan has six, and a
                // hardcoded two rows clipped the top three off.
                rows={Math.ceil(steps.length / 3)}
                activeIndex={steps.findIndex((st) => st.speed === speed)}
                onStep={(m) => publishCommand(fan.uniqueNumber, m, userId)}
              />
            );
          }

          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={b ? b.WallButtonId : `empty-${i}`}
              onClick={() => b && onButton(b, on)}
              sx={{
                /*
                 * ── A toggle tile is COLOURED by its load ──────────────────
                 * Green on, soft red off. The word stays — colour alone is not
                 * a label, and about one man in twelve cannot separate these
                 * two hues — but from across a room the colour is what is
                 * readable, and reading a wall panel from across the room is
                 * the entire job.
                 *
                 * The red is deliberately muted (#B4564F, not a signal red): on
                 * a sheet of sixteen tiles, most of them off most of the time, a
                 * saturated red reads as sixteen alarms.
                 *
                 * ⚠️ A toggle's own ButtonColor is IGNORED while it has a state
                 * to show — the state is worth more than the decoration, and a
                 * green-painted button that is off would say two things at once.
                 * Every other kind of button (momentary, scene, navigate) keeps
                 * its colour, because it has no state to contradict it.
                 */
                bgcolor: toggleColor(b, target, on),
                color: b ? (b.TextColor || '#fff') : 'transparent',
                borderRadius: 2, display: 'flex', alignItems: 'center',
                justifyContent: 'center', flexDirection: 'column',
                textAlign: 'center', p: 1, userSelect: 'none', fontWeight: 600,
                cursor: b ? 'pointer' : 'default',
                minHeight: 0, minWidth: 0, overflow: 'hidden',
                fontSize: 'clamp(12px, 2.2vw, 22px)',
                // UNKNOWN is dimmed rather than drawn as off — a sheet that
                // shows everything off invites someone to press what is already
                // on. It keeps the neutral slate, so it cannot be mistaken for
                // either colour.
                opacity: target && on === null ? 0.45 : 1,
                transition: 'background-color 160ms',
                position: 'relative',
              }}
            >
              {/* Is the device answering at all? Independent of on/off — a
                  switch can be OFF and healthy, or ON in the row and unplugged. */}
              {deviceOf(b) && <LiveDot uniqueNumber={deviceOf(b)} />}
              {b ? b.ButtonText : ''}
              {target && (
                <Box component="span" sx={{ fontSize: '0.6em', fontWeight: 700, opacity: 0.95, letterSpacing: 0.5 }}>
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {on === null ? '—' : on ? 'ON' : 'OFF'}
                </Box>
              )}
            </Box>
          );
        }) : panel.Buttons.map((b) => (

          <Box
            key={b.WallButtonId}
            onClick={() => onButton(b)}
            sx={{
              gridColumn: `span ${Math.min(b.Width, cols)}`,
              gridRow: `span ${b.Height}`,
              bgcolor: b.ButtonColor || '#37474F',
              color: b.TextColor || '#fff',
              borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
              textAlign: 'center', p: 1, cursor: 'pointer', userSelect: 'none', fontWeight: 600,
            }}
          >
            {b.ButtonType === 'navigate' ? `▶ ${b.ButtonText}` : b.ButtonText}
          </Box>
        ))}
      </Box>

      {/* Pager */}
      <Box sx={{ position: 'absolute', bottom: 6, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <IconButton size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)} sx={{ color: colors.text }}><ChevronLeftIcon /></IconButton>
        {panels.map((_, i) => (
          <Box key={i} sx={{ width: i === page ? 18 : 6, height: 6, borderRadius: 3, bgcolor: i === page ? colors.button : 'rgba(255,255,255,0.4)' }} />
        ))}
        <IconButton size="small" disabled={page === panels.length - 1} onClick={() => setPage((p) => p + 1)} sx={{ color: colors.text }}><ChevronRightIcon /></IconButton>
      </Box>

      {/* Tap-to-wake overlay */}
      {screenOff && (
        <Box onClick={() => setScreenOff(false)} sx={{ position: 'absolute', inset: 0, bgcolor: '#000', zIndex: 10 }} />
      )}
    </Box>
  );
}
